import { useEffect, useState } from "react";

import { type RegisteredComponent, isEditing } from "@builder.io/sdk-react";

import { ModelMap } from "~/hec/builder/model-map";
import { Carousel, CarouselContent } from "~/hec/components/ui/carousel";
import type { CarouselApi } from "~/hec/components/ui/carousel.utils";
import { useFetchQuestion } from "~/hooks/learning-center/use-fetch-lms";
import { useMediaQuery } from "~/lib/utils/screens";
import { useRootLayoutData } from "~/root";

import GoalBenefitsTable from "./goal-benefits-table";
import GoalQuestion from "./goal-question";
import GoalResult from "./goal-result";
import { appendNextQuestion, truncateQuestions } from "./questionnaire-logic";
import type {
  BuilderReference,
  DisclaimerItem,
  GoalData,
  QuestionData,
} from "./types";
import { useQuestionnaireData } from "./use-questionnaire-data";

/**
 * GoalQuestionnaire — the HSA "What's My HSA Goal?" quiz (SFCC-11290).
 * Ported from hec-composable `questionnaire/goal/goalQuestionnaire.jsx`.
 *
 * Builder `noWrap` block (registered as "GoalQuestionnaire" — the name live HSA
 * content references). Reads its inputs as props and spreads `attributes` on the
 * root, per the noWrap contract.
 *
 * Flow: an embla carousel of question slides. `firstQuestion` seeds it (inlined
 * value, or lazy-fetched by id). Each answer branches to the next question
 * (appended + advanced) or a goal (the recommended-goal panel). Goal + benefit
 * data is pre-loaded once (`useQuestionnaireData`) and feeds both the benefits
 * table and answer→goal resolution.
 *
 * All resolved questions render as carousel slides; the visible slide is driven
 * by `currentIndex` (the answer handlers bump it), and an effect calls
 * `api.scrollTo(currentIndex)` so the programmatic advance and Back navigation
 * animate as a horizontal slide (SFCC-11730 — restores the embla wiring dropped
 * in the SFCC-11290 port). Drag is disabled (`watchDrag: false`), matching
 * composable — the carousel advances only programmatically.
 */
interface GoalQuestionnaireProps {
  attributes?: Record<string, unknown> & { className?: string };
  firstQuestion?: BuilderReference<QuestionData>;
  disclaimerText?: DisclaimerItem[];
  benefitsTableBgImage?: string;
}

const GoalQuestionnaire = ({
  attributes,
  firstQuestion,
  disclaimerText,
  benefitsTableBgImage,
}: GoalQuestionnaireProps) => {
  const { className, ...restAttributes } = attributes ?? {};
  const isMobile = useMediaQuery("(max-width: 991px)");
  const { isRegistered } = useRootLayoutData();
  // Treat the Builder editor canvas as non-guest so the "Start Learning" CTA
  // (not the sign-in prompt) is authorable/visible there.
  const isGuest = !isRegistered && !isEditing();

  const { goals, benefits, isLoading: isDataLoading } = useQuestionnaireData();

  // firstQuestion: prefer an inlined value; else lazy-fetch by id.
  const inlineFirst = firstQuestion?.value?.data;
  const { data: firstQuestionEntry, isLoading: isFirstQuestionLoading } =
    useFetchQuestion(!inlineFirst ? firstQuestion?.id : undefined);
  const firstQuestionContent = (inlineFirst ??
    (firstQuestionEntry?.data as QuestionData | undefined)) as
    | QuestionData
    | undefined;

  const [questions, setQuestions] = useState<QuestionData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [goal, setGoal] = useState<GoalData | null>(null);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  // Seed the first question once it resolves.
  useEffect(() => {
    if (firstQuestionContent && questions.length === 0) {
      setQuestions([firstQuestionContent]);
    }
  }, [firstQuestionContent, questions.length]);

  // Drive the slide position from `currentIndex` so the programmatic advance
  // (answer → setCurrentIndex+1) and Back (setCurrentIndex-1) animate as a
  // horizontal slide. A freshly appended slide isn't measured by embla until
  // after layout, so `reInit` once before scrolling to the new index — this
  // mirrors the composable's slidesChanged→setTimeout(300)→scrollNext wiring,
  // now expressed declaratively from the index we already track.
  useEffect(() => {
    if (!carouselApi) return;
    carouselApi.reInit();
    carouselApi.scrollTo(currentIndex);
  }, [carouselApi, currentIndex, questions.length]);

  // Answer → next question: truncate any later (post-Back) questions, append the
  // next, and advance. Clearing `goal` mirrors composable (a question branch is
  // not terminal).
  const handleSelectQuestion = (next: QuestionData) => {
    setGoal(null);
    setQuestions(prev => appendNextQuestion(prev, currentIndex, next));
    setCurrentIndex(i => i + 1);
  };

  // Answer → goal: terminal. Trim later questions and surface the recommendation.
  const handleSelectGoal = (selected: GoalData) => {
    setQuestions(prev => truncateQuestions(prev, currentIndex));
    setGoal(selected);
  };

  const goNext = () =>
    setCurrentIndex(i => (i + 1 < questions.length ? i + 1 : i));
  const goBack = () => setCurrentIndex(i => Math.max(0, i - 1));

  const current = questions[currentIndex];
  // Hold the quiz behind the loader until the goal/benefit list is in too —
  // an answer that branches to a goal resolves against that list, so it must be
  // present before any answer is clickable (composable gated all data likewise).
  const showLoader =
    isDataLoading || (isFirstQuestionLoading && !inlineFirst) || !current;

  return (
    <div className={className} {...restAttributes}>
      {showLoader ? (
        <div className="mx-auto mt-4">
          <div className="my-20 flex w-full justify-center">
            <div
              className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-accent"
              data-testid="goalQuestionnaire-Loader"
            />
          </div>
        </div>
      ) : (
        <Carousel
          opts={{ watchDrag: false }}
          setApi={setCarouselApi}
          className="h-auto"
          data-testid="goalQuestionnaire-Carousel"
        >
          <CarouselContent className="mx-auto mt-4">
            {questions.map((question, index) => (
              <GoalQuestion
                key={`question-${index}`}
                question={question}
                isFirst={index === 0}
                isLast={index === questions.length - 1}
                goal={goal}
                isMobile={isMobile}
                isGuest={isGuest}
                goals={goals}
                onSelectQuestion={handleSelectQuestion}
                onSelectGoal={handleSelectGoal}
                onAdvance={goNext}
                onBack={goBack}
              />
            ))}
          </CarouselContent>
        </Carousel>
      )}

      {isMobile && goal && (
        <GoalResult goal={goal} isMobile={isMobile} isGuest={isGuest} />
      )}

      {!isDataLoading && (
        <GoalBenefitsTable
          currentGoal={goal}
          bgImage={benefitsTableBgImage}
          isMobile={isMobile}
          benefits={benefits}
          goals={goals}
          disclaimerText={disclaimerText}
        />
      )}
    </div>
  );
};

export default GoalQuestionnaire;

export const GoalQuestionnaireDefinition = {
  component: GoalQuestionnaire,
  name: "GoalQuestionnaire",
  noWrap: true,
  inputs: [
    {
      name: "firstQuestion",
      type: "reference",
      model: ModelMap.learningCenterQuestionnaireQuestion,
      friendlyName: "First Question",
      helperText: "Starting question for the quiz.",
      required: true,
    },
    {
      name: "disclaimerText",
      type: "list",
      subFields: [{ name: "text", type: "longText", required: true }],
    },
    {
      name: "benefitsTableBgImage",
      friendlyName: "Benefits Table Background Image",
      type: "file",
    },
  ],
} satisfies RegisteredComponent;
